const normalizeGroup = (group: string): string => group.trim().toLowerCase()

const isAdminGroup = (group: string): boolean => normalizeGroup(group) === "admin"

export type VerifiedPlatformActorKind = "merchant" | "superadmin" | null

export const resolveBackendActorResolutionPlan = (
  actorKind: VerifiedPlatformActorKind,
  hasSelfServiceSignupIntent: boolean
): {
  shouldProvisionSelfServiceOrganization: boolean
  shouldRetryActorResolution: boolean
} => ({
  shouldProvisionSelfServiceOrganization: actorKind === null && hasSelfServiceSignupIntent,
  shouldRetryActorResolution: actorKind === null,
})

/** JWT group claims are identity hints, not authorization for platform administration. */
export const removeUnverifiedAdminGroup = (groups: string[]): string[] =>
  groups.filter((group) => !isAdminGroup(group))

/** Add Admin only after the backend returned explicit admin authorization evidence. */
export const addVerifiedAdminGroup = (groups: string[]): string[] => [
  ...removeUnverifiedAdminGroup(groups),
  "Admin",
]

/** Resolve navigation groups from the backend actor, never from a raw JWT Admin claim. */
export const resolveBackendVerifiedUserGroups = (
  claimedGroups: string[],
  actorKind: VerifiedPlatformActorKind
): {
  groups: string[]
  hasUnverifiedAdminClaim: boolean
  isAdmin: boolean
} => {
  const groups = removeUnverifiedAdminGroup(claimedGroups)
  const isAdmin = actorKind === "superadmin"

  return {
    groups: isAdmin ? addVerifiedAdminGroup(groups) : groups,
    hasUnverifiedAdminClaim: claimedGroups.some(isAdminGroup),
    isAdmin,
  }
}

export const hasAnyRequiredGroup = (userGroups: string[], requiredGroups: string[]): boolean => {
  const normalizedRequiredGroups = new Set(requiredGroups.map(normalizeGroup))

  return userGroups.some((group) => normalizedRequiredGroups.has(normalizeGroup(group)))
}
